import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError } from '#src/shared/error';

import type { PsfShareDataQuery, PsfShareDataQueryVariables } from './api.graphql';
import { PsfShareData } from './api.graphql';
import type { ShareFeedItem } from './shareFeed';
import { getShareFeed } from './shareFeed';
import { prefetchShareGoal } from './shareGoal';

type Petition = NonNullable<PsfShareDataQuery['petition']>;
type FeatureConfigs = PsfShareDataQuery['featureConfigs'];
type Viewer = PsfShareDataQuery['viewer'];
type Experiments = PsfShareDataQuery['experiments'];
export type PsfSharePageData = {
	petition: Omit<Petition, 'tagsConnection'> & { categoryTags: string[] };
	shareFeed: readonly ShareFeedItem[];
	/**
	 * false on the message view (a non-empty `share` URL param), which the default flow navigates to after the first share
	 */
	initial: boolean;
	/**
	 * true when coming from the promote_or_share page
	 */
	skip: boolean;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimate: number;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimateLocalized: string;
	/**
	 * Feature configuration flags
	 */
	featureConfigs: FeatureConfigs;
	/**
	 * Resolved experiments, passed through untreated. Both are fetched with `treat: false` and treated
	 * client-side at their exposure moment, so the treated population matches the exposed one:
	 * `revenueSharePromoteSlideUpExperiment` when the promote slide-up is triggered,
	 * `psfShareGoalCounterExperiment` when `SharingGoalFlow` mounts on the initial view.
	 */
	experiments: Experiments;
	/**
	 * Chip-in amount offered by the promote slide-up, in the viewer's local currency units.
	 */
	chipInAmount: number;
	/**
	 * Localized rendering of `chipInAmount`, e.g. "$3".
	 */
	chipInAmountLocalized: string;
	/**
	 * ISO currency code `chipInAmount` is denominated in.
	 */
	chipInCurrencyCode: string;
	/**
	 * Rate `chipInAmount` converts to USD by, for money-event normalization.
	 */
	chipInExchangeRate: number;
	/**
	 * Pre-fetched sharing goal for the current viewer on this petition.
	 * null when the viewer has no saved goal, is not identified, or the fetch failed.
	 */
	initialShareGoal: number | null;
};
/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

const CHIP_IN_USD_FALLBACK = 3;

export async function getPsfSharePageData(
	utilityContext: UtilityContext,
	slug: string,
	{
		queryParams,
		countryCode,
		locale,
	}: {
		queryParams: { skip?: string; share?: string };
		countryCode: string;
		locale: string;
	},
): Promise<PsfSharePageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const skip = !!queryParams.skip;
	const initial = !queryParams.share;

	const [{ data }, shareFeed] = await Promise.all([
		fetch<PsfShareDataQuery, PsfShareDataQueryVariables>({
			query: PsfShareData,
			variables: {
				slugOrId: slug,
				countryCode,
				locale,
			},
			rejectOnError: true,
		}),
		getShareFeed(utilityContext, slug),
	]);

	if (!data.petition) {
		throw new NotFoundError();
	}

	const normalized = normalizeData(utilityContext, {
		skip,
		initial,
		petition: data.petition,
		shareFeed,
		featureConfigs: data.featureConfigs,
		viewer: data.viewer,
		experiments: data.experiments,
	});
	const initialShareGoal = await prefetchShareGoal(utilityContext, data.petition.id);

	return { ...normalized, initialShareGoal };
}

function normalizeData(
	utilityContext: UtilityContext,
	{
		skip,
		initial,
		petition,
		shareFeed,
		featureConfigs,
		viewer,
		experiments,
	}: {
		skip: boolean;
		initial: boolean;
		petition: Petition;
		shareFeed: readonly ShareFeedItem[];
		featureConfigs: FeatureConfigs;
		viewer: Viewer;
		experiments: Experiments;
	},
): Omit<PsfSharePageData, 'initialShareGoal'> {
	const { i18n } = utilityContext;
	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);

	const { tagsConnection, ...rest } = petition;

	return {
		petition: {
			...rest,
			categoryTags: tagsConnection?.nodes.map((tag) => tag.name) ?? [],
		},
		shareFeed,
		skip,
		initial,
		signaturesFromShareCountEstimate,
		signaturesFromShareCountEstimateLocalized: i18n.localizeNumber(signaturesFromShareCountEstimate),
		featureConfigs,
		experiments,
		...normalizeChipInAmount(utilityContext, viewer),
	};
}

type Currency = Pick<NonNullable<Viewer['country']['currency']>, 'code' | 'exchangeRate'>;

const DEFAULT_CURRENCY: Currency = { code: 'USD', exchangeRate: 1 };

function normalizeChipInAmount(
	{ i18n }: UtilityContext,
	viewer: Viewer,
): {
	chipInAmount: number;
	chipInAmountLocalized: string;
	chipInCurrencyCode: string;
	chipInExchangeRate: number;
} {
	const { currency, promotionCountrySettings } = viewer.country;

	// A currency with no exchange rate loaded cannot be converted: the USD fallback would become $0
	// (and a tracked amount of Infinity), so it is treated as absent, like a missing currency.
	const usableCurrency = currency && currency.exchangeRate > 0 ? currency : undefined;
	// The amount choices are denominated in the viewer's own currency, so without a usable one they
	// cannot be read: an IDR ladder would render as "$50,000" and report a wildly wrong USD amount.
	const localChoices = usableCurrency ? promotionCountrySettings?.amountChoices : undefined;
	// A zero or negative choice would render a "chip in $0" CTA, so it is not a usable offer.
	const positiveChoices = (localChoices ?? []).filter((amount) => amount > 0);
	const smallestPositiveChoice = positiveChoices.length > 0 ? Math.min(...positiveChoices) : undefined;

	const { code, exchangeRate }: Currency = usableCurrency ?? DEFAULT_CURRENCY;
	const chipInAmount = smallestPositiveChoice ?? Math.round(CHIP_IN_USD_FALLBACK * exchangeRate);

	return {
		chipInAmount,
		chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, code),
		chipInCurrencyCode: code,
		chipInExchangeRate: exchangeRate,
	};
}
